import { AxiosInstance } from 'axios';

import { productSkuIds } from '@/lib/constants';
import {
  channelJSONConfigService,
  channelsJSONConfigService,
  sentryService,
} from '@/services/index';
import { useCustomerStore } from '@/stores';
import { T_Channel } from '@/types';

export class CommerceToolsService {
  private ctAgent: AxiosInstance;
  constructor(ctAgent: AxiosInstance) {
    this.ctAgent = ctAgent;
  }

  private updateKkExpiry = (response: any) => {
    let ctAPIStatus = response?.status === 404 || response?.status === '404';
    useCustomerStore
      .getState()
      .setKkExpireAt(
        response?.result?.lastModifiedAt,
        response?.result?.deleteDaysAfterLastModification,
        ctAPIStatus
      );
  };

  private captureError(method: string, error: unknown, extra?: Record<string, unknown>) {
    const axiosError = error as {
      config?: { method?: string; url?: string; data?: unknown };
      response?: { status?: number; statusText?: string; data?: unknown };
    };
    const endpoint = typeof extra?.endpoint === 'string' ? extra.endpoint : undefined;
    const payload =
      typeof extra?.payload === 'object' && extra.payload !== null
        ? (extra.payload as Record<string, unknown>)
        : undefined;
    const source =
      typeof extra?.source === 'string'
        ? extra.source
        : typeof payload?.source === 'string'
          ? payload.source
          : undefined;
    const customerType =
      typeof extra?.customerType === 'string'
        ? extra.customerType
        : typeof payload?.customerType === 'string'
          ? payload.customerType
          : undefined;
    const status = axiosError?.response?.status;

    sentryService.captureException(error, {
      tags: {
        service: 'CommerceToolsService',
        ct_method: method,
        ...(endpoint ? { ct_endpoint: endpoint } : {}),
        ...(source ? { source } : {}),
        ...(customerType ? { customer_type: customerType } : {}),
        ...(typeof status === 'number' ? { http_status: String(status) } : {}),
      },
      contexts: {
        service: 'CommerceToolsService',
        method,
        request: axiosError?.config
          ? {
              method: axiosError.config.method,
              url: axiosError.config.url,
              data: axiosError.config.data,
            }
          : undefined,
        response: axiosError?.response
          ? {
              status: axiosError.response.status,
              statusText: axiosError.response.statusText,
              data: axiosError.response.data,
            }
          : undefined,
        ...extra,
      },
    });
  }

  public getTempCustomerId = async (
    customerType: string,
    source: string,
    accountNumber?: string
  ) => {
    try {
      const response = await this.ctAgent.post<{
        result: {
          customerId: string;
        };
        code: string;
        message: string;
        description: null;
      }>('/createCart', {
        source,
        customerType,
        accountNumber,
      });
      this.updateKkExpiry(response.data);
      return response.data?.result?.customerId;
    } catch (error) {
      this.captureError('getTempCustomerId', error, {
        endpoint: '/createCart',
        source,
        customerType,
        accountNumber,
      });
      throw error;
    }
  };

  public getAllProduct = async (overrideProductInfoFromKK: boolean) => {
    try {
      // Get CT product information
      const { data } = await this.ctAgent.get<any>('/products', { params: { channel: 'web' } });
      const result = data?.result ?? [];
      if (overrideProductInfoFromKK) {
        try {
          const [channelIconList, channels] = await Promise.all([
            channelJSONConfigService.getChannelConfig(),
            channelsJSONConfigService.getChannelConfig(),
            // myAccountV2Service.getCardContainer({ usePreview: false }),
          ]);

          for (let product of result) {
            product.sku = product.code;
            product.price0 = product.priceIncTax;
            product.quantity = 0;
            product.quantityInCart = 0;
            product.image = product.attributes.image;
            product.model = product.attributes.model;
            product.price = product.priceIncTax;
            product.description = product.attributes.description;
            product.categoryName = product.category;
            product.custom9 = product.productCustom9;
            product.custom1 = product.attributes.image;
            /* let prod = cardSection.data?.cardContainer.find((d: any) => d?.sku === product.sku);
            product.videoUrl = prod?.videoUrl ?? product?.videoUrl;
            product.horizontalVideoUrl = prod?.horizontalVideoUrl ?? product.horizontalVideoUrl;
            product.horizontalImageUrl = prod?.horizontalImageUrl?.url ?? product.horizontalImageUrl;
            product.custom1 = prod?.mainImageUrl?.url ?? product.custom1;
            product.logoImageUrl = prod?.logoImageUrl?.url ?? product.logoImageUrl;
            product.description =
              prod?.description && prod?.description !== '' ? prod.description : product.description;
            product.explorePopupCopy = prod?.explorePopupCopy ?? product.explorePopupCopy;
            product.name = prod?.name && prod?.name !== '' ? prod.name : product.name;
            product.contractDescription = prod?.contractDescription ?? product.contractDescription;
            product.showExplorePopup =
              prod?.showExplorePopup && prod?.showExplorePopup ? true : false;
            product.linkLabel = prod?.linkLabel ?? 'Explore';
            product.linkUrl = prod?.linkUrl ?? product.linkUrl;
            product.iconImageUrl = prod?.iconImageUrl?.map((img: any) => img.iconImageUrl);
            product.popupCards = prod?.popupCards?.map((items: any) => ({
              ...items,
              popupCardIconURL: items.popupCardIconUrl.url,
              popupCardImageURL: items.popupCardImageUrl.url,
            }));*/

            product.channels = channels.filter((c: T_Channel) => c.sku === product.sku);

            // Add channel list icon
            switch (product.sku) {
              case productSkuIds.starter.primary:
                product.iconImageUrlFromJSON = channelIconList?.Starter;
                break;
              case productSkuIds.sport.primary:
                product.iconImageUrlFromJSON = channelIconList?.Sport;
                break;
              case productSkuIds.movies.primary:
                product.iconImageUrlFromJSON = channelIconList?.Movies;
                break;
              case productSkuIds.entertainment.primary:
                product.iconImageUrlFromJSON = channelIconList?.Entertainment;
                break;
              case productSkuIds.soho.primary:
                product.iconImageUrlFromJSON = channelIconList?.SoHo;
                break;
            }
          }
        } catch (error) {
          this.captureError('getAllProduct.overrideProductInfoFromKK', error, {
            endpoint: '/products',
            channel: 'web',
            overrideProductInfoFromKK,
          });
          // Preserve the CT product data if the enrichment layer fails.
        }
      }
      return result;
    } catch (error) {
      this.captureError('getAllProduct', error, {
        endpoint: '/products',
        channel: 'web',
        overrideProductInfoFromKK,
      });
      throw error;
    }
  };

  public updateOrder = async (
    customerId: string,
    codes: Array<{ code: string; quantity: number; action: number }>,
    source: string,
    customerType: string,
    campaigns: any[] = [],
    productType: string = '',
    removeVoucher?: boolean
  ) => {
    const payload: Record<string, any> = {
      customerId,
      codes,
      source,
      customerType,
      campaigns,
      productType,
      removeVoucher,
    };
    try {
      const { data } = await this.ctAgent.put<any>('/updateCart', payload);
      this.updateKkExpiry(data);
      return data.result;
    } catch (error) {
      this.captureError('updateOrder', error, {
        endpoint: '/updateCart',
        payload,
      });
      throw error;
    }
  };

  public getCustomerOrder = async (
    customerId: string,
    source: string = 'web',
    accountNumber?: string
  ) => {
    const payload = {
      customerId,
      source,
    };
    try {
      const { data } = await this.ctAgent.post<any>('/getCart', payload);
      this.updateKkExpiry(data);
      return data.result;
    } catch (error) {
      this.captureError('getCustomerOrder', error, {
        endpoint: '/getCart',
        payload,
        accountNumber,
      });
      throw error;
    }
  };
}
